import React, { useState, useEffect, useRef } from "react";
import "./index.css";
import VapiWidget from './VapiWidget.tsx';
import { FaGlobe, FaBriefcase, FaRobot, FaMicrophone, FaVolumeUp, FaBookOpen, FaExclamationCircle, FaSync, FaPhone } from "react-icons/fa";

const introQuestions = [
  { title: "Hey! Parabéns pela escolha do curso! 😃", 
    subtitle:"Conte conosco para evoluir no seu aprendizado e atingir seus objetivos! 🚀", 
    prequestion: "Agora antes de começar vamos entender seu nível e maior necessidade hoje com o inglês.",
    question: "Me conta, qual sua maior necessidade hoje para evoluir com o inglês?", 
    options: ["Inglês geral", "Inglês para negócios, crescimento profissional"] },
  { title: "THANKS!",
    question: "Agora me conta, você gostaria de iniciar do nível Zero ou realizar o teste para saber o seu nível atual de inglês?", 
    options: ["Quero fazer o teste!", "Quero começar do zero"] }
];

const questions = [
  { question: "Can I leave my bag here?", options: ["Only for a few minutes", "I hope so", "It’s not very heavy", "I already did"], answer: 0 },
  { question: "What colour will you paint the office walls?", options: ["We’re still thinking about it", "It’s almost finished", "I didn’t see it", "It’s very nice"], answer: 0 },
  { question: "I can’t log in to my account.", options: ["Do you want me to help?", "You should know that", "Yes, you can", "Not at all"], answer: 0 },
  { question: "His legs were ___ weak that he couldn’t climb the stairs.", options: ["such", "too", "so", "very"], answer: 2 },
  { question: "The team must decide ___ and for all how to approach this problem.", options: ["here", "once", "first", "finally"], answer: 1 },
  { question: "Don’t leave your phone on the ___ of the sofa – it might fall.", options: ["outside", "edge", "boundary", "border"], answer: 1 },
  { question: "I’m sorry – I didn’t ___ to interrupt the meeting.", options: ["hope", "think", "mean", "suppose"], answer: 2 },
  { question: "The speaker ended the seminar ___ a short Q&A session.", options: ["by", "with", "in", "as"], answer: 1 },
  { question: "Would you mind ___ the table before our guests arrive?", options: ["making", "doing", "getting", "giving"], answer: 1 },
  { question: "I was looking forward ___ a coffee with you, but you had to leave.", options: ["to have", "to having", "having", "to had"], answer: 1 },
  { question: "___ busy John is at work, he always replies to important emails.", options: ["Whatever", "No matter how", "However much", "Although"], answer: 1 },
  { question: "It was just last week ___ she moved into her new apartment.", options: ["then", "since", "after", "that"], answer: 3 },
  { question: "The store didn’t have my size, but they’ve ___ one from another branch.", options: ["booked", "ordered", "commanded", "asked"], answer: 1 },
  { question: "Once the train leaves the station, you can ___ your seat belt.", options: ["undress", "unfasten", "unlock", "untie"], answer: 1 },
  { question: "The proposal was ___ rejected by the investors.", options: ["strongly", "firmly", "unanimously", "agreed"], answer: 2 },
  { question: "If we don’t address the issue now, it could ___ the entire project.", options: ["jeopardize", "promote", "secure", "approve"], answer: 0 },
  { question: "The new regulation aims to ensure data privacy and protect consumer ___ .", options: ["rights", "writings", "rites", "roles"], answer: 0 },
  { question: "The report offers a(n) ___ analysis of the economic downturn.", options: ["superficial", "in-depth", "shallow", "vague"], answer: 1 },
  { question: "The CEO’s speech was so ___ that it inspired immediate action.", options: ["compelling", "compellingly", "compel", "compels"], answer: 0 },
  { question: "By ___ the company’s core values, the leadership fostered trust and loyalty.", options: ["embodying", "embodied", "embodies", "embodiment"], answer: 0 }
];

const levels = [
  { min: 0, max: 3, level: "A1 - Beginner", desc: "Você compreende frases simples e vocabulário básico.", pdf: "/pdfs/A1.pdf" },
  { min: 4, max: 6, level: "A2 - Elementary", desc: "Você consegue lidar com situações simples do dia a dia.", pdf: "/pdfs/A2.pdf" },
  { min: 7, max: 10, level: "B1 - Intermediate", desc: "Você consegue se comunicar em situações comuns de trabalho e vida social.", pdf: "/pdfs/B1.pdf" },
  { min: 11, max: 14, level: "B2 - Upper-Intermediate", desc: "Você se comunica de forma clara e detalhada em diversos contextos.", pdf: "/pdfs/B2.pdf" },
  { min: 15, max: 17, level: "C1 - Advanced", desc: "Você possui excelente domínio do idioma e entende nuances de linguagem.", pdf: "/pdfs/C1.pdf" },
  { min: 18, max: 20, level: "C2 - Proficient", desc: "Você compreende e se expressa com fluência quase nativa.", pdf: "/pdfs/C2.pdf" }
];

export default function App() {
  const [phase, setPhase] = useState("intro");
  const [introCurrent, setIntroCurrent] = useState(0);
  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [objective, setObjective] = useState("general");
  const [conversationFinished, setConversationFinished] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [conversationSummary, setConversationSummary] = useState(null);
  const showConversationSummary = false;
  const [callId, setCallId] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [retrying, setRetrying] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(15);
  const [adjustedLevel, setAdjustedLevel] = useState(null);
  const [overallScore, setOverallScore] = useState(null);
  const [hasStartedWidgetOnce, setHasStartedWidgetOnce] = useState(false);
  const countdownInterval = useRef(null);
  const countdownRef = useRef(countdown);
  const retryCountdownRef = useRef(retryCountdown);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  useEffect(() => {
    retryCountdownRef.current = retryCountdown;
  }, [retryCountdown]);

  const getAssistantId = (level, objective) => {
    const generalMap = {
      "A2": "b411accf-de72-422b-8cec-06465bba0142",
      "B1": "657e2918-5652-4bdd-ac51-2a48c5b61e49",
      "B2": "1d82d510-e1da-4476-8aad-eabff1cec3c4",
      "C1": "310a64af-d62b-4c73-a8c7-959443ccffa4",
      "C2": "68cc9365-edac-42d8-bd11-290c6df86536"
    };
    const businessMap = {
      "A2": "d5847e27-126a-4a6b-892f-b4e22cf111cb",
      "B1": "f02ba060-b142-4444-aff6-cac97efad444",
      "B2": "17860fe5-9dfc-445e-bfa3-2601304e645e",
      "C1": "1f89ff25-4b86-4d1b-85aa-e23fdffdde08",
      "C2": "36e099a4-8864-4548-88f5-1e9b6fee69fd"
    };
    if (objective === "business") {
      return businessMap[level] || "";
    }
    return generalMap[level] || "";
  };

  const handleIntroAnswer = (index) => {
    if (introCurrent === 0) {
      if (index === 0) {
        setObjective("general");
      } else {
        setObjective("business");
      }
      setIntroCurrent(1);
    } else if (introCurrent === 1) {
      if (index === 1) {
        setPhase("zerostart");
      } else {
        setPhase("test");
      }
    }
  };

  const handleAnswer = (index) => {
    if (phase !== "test") return;
    if (index === questions[current].answer) setScore(score + 1);
    if (current + 1 < questions.length) setCurrent(current + 1);
    else {
      setFinished(true);
      setPhase("finished");
    }
  };

  const getLevel = () => levels.find(l => score >= l.min && score <= l.max);

  useEffect(() => {
    if (waiting) {
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(countdownInterval.current);
      setCountdown(30);
    }
    return () => clearInterval(countdownInterval.current);
  }, [waiting]);

  useEffect(() => {
    let retryInterval;
    if (retrying) {
      retryInterval = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(retryInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(retryInterval);
      setRetryCountdown(15);
    }
    return () => clearInterval(retryInterval);
  }, [retrying]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6">
      <img src="/img/natics-logo.png" alt="Natics Logo" className="w-64 mb-4" />
      <h1 className="text-2xl font-bold mb-12">Teste de Nivelamento</h1>

      {phase === "intro" && (
        <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg w-full max-w-xl">
          {introQuestions[introCurrent].title && (<h2 className="text-2xl font-bold mb-2">{introQuestions[introCurrent].title}</h2>) }
          {introQuestions[introCurrent].subtitle && (<h2 className="text-xl mb-4">{introQuestions[introCurrent].subtitle}</h2>) }
          <h2 className="text-md mb-4">{introQuestions[introCurrent].prequestion}</h2>
          <h2 className="text-md text-[#42A48B] mb-4">{introQuestions[introCurrent].question}</h2>
          <div className="grid gap-3">
            {introQuestions[introCurrent].options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleIntroAnswer(i)}
                className="w-full p-3 rounded-2xl bg-gray-800 hover:bg-gray-700 transition"
              >
                {introCurrent === 0 && i === 0 ? <><FaGlobe className="inline-block mr-2" />{opt}</> :
                 introCurrent === 0 && i === 1 ? <><FaBriefcase className="inline-block mr-2" />{opt}</> :
                 opt}
              </button>
            ))}
          </div>
          <p className="mt-4 text-sm text-gray-400">Pergunta {introCurrent + 1} de {introQuestions.length}</p>
        </div>
      )}

      {phase === "test" && !finished && (
        <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg w-full max-w-xl">
          <h2 className="text-xl mb-4">{questions[current].question}</h2>
          <div className="grid gap-3">
            {questions[current].options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleAnswer(i)}
                className="w-full p-3 rounded-2xl bg-gray-800 hover:bg-gray-700 transition"
              >
                {opt}
              </button>
            ))}
          </div>
          <p className="mt-4 text-sm text-gray-400">Pergunta {current + 1} de {questions.length}</p>
        </div>
      )}

      {phase === "zerostart" && (
        <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg text-center">
          <p className="text-xl mb-6">All right! Vamos iniciar os estudos do zero.</p>
          <button className="p-4 rounded-xl bg-[#42A48B] hover:bg-[#368c73] shadow-lg transform hover:scale-105 transition">
            <a href="/pdfs/A1.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center">
              <FaBookOpen className="mr-2" />
              Baixar plano de estudos (A1)
            </a>
          </button>
        </div>
      )}

      {phase === "finished" && !conversationFinished && !waiting && (
        <div className="w-full max-w-xl text-center">
          {getLevel().level.startsWith("A1") ? (
            <div className="text-center">
              <p className="text-xl mb-2">Você acertou {score} de {questions.length} perguntas de múltipla escolha.</p>
              <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg mt-6">
                <p className="text-lg font-bold text-[#42A48B]">Nível final: {getLevel().level}</p>
                <button className="mt-6 p-4 rounded-xl bg-[#42A48B] hover:bg-[#368c73] shadow-lg transform hover:scale-105 transition">
                  <a href={getLevel().pdf} target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <FaBookOpen className="mr-2" />
                    Baixar plano de estudos ({getLevel().level})
                  </a>
                </button>
              </div>
            </div>
          ) : (
            <>
              {!conversationStarted && (
                <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg max-w-xl text-center mx-auto">
                  {!hasStartedWidgetOnce && (<h2 className="text-xl font-semibold mb-2">Obrigado!</h2>)}
                  <h3 className="text-2xl font-bold text-[#42A48B] mb-6">
                    Prepare-se para o teste de conversação
                  </h3>

                  <div className="space-y-1 text-gray-300 text-left mb-6">
                    <p><FaRobot className="inline-block text-[#42A48B] mr-2" />Você participará de uma chamada com a <span className="font-semibold">professora virtual</span>.</p>
                    <p><FaMicrophone className="inline-block text-[#42A48B] mr-2" />Esteja preparado para falar em inglês e responder às perguntas da IA.</p>
                    <p><FaVolumeUp className="inline-block text-[#42A48B] mr-2" />Procure um local silencioso e fale alto e claro.</p>
                  </div>

                  <p className="text-gray-400 text-left mb-4">
                    Lembre-se de verificar o volume e microfone do seu dispositivo, 
                    ou utilizar um fone de ouvido.
                  </p>

                  <div className="text-left bg-black font-medium rounded-lg p-3 mb-4">
                    <FaExclamationCircle className="inline-block text-[#42A48B] mr-1" /> ATENÇÃO: Caso não entenda a pergunta, responda com 
                    “I DON’T KNOW”, “I DON’T UNDERSTAND” ou “REPEAT PLEASE”.
                  </div>
                </div>
              )}
              {renderVapiWidget()}
            </>
          )}
        </div>
      )}

      {waiting && (
        <>
          <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg max-w-xl text-center mx-auto mt-2 text-lg text-[#42A48B]">
            <p className="text-xl text-white">Por favor aguarde</p>
            {retrying ? (<p>Parece que a sua avaliação ainda não está pronta. Aguarde mais {retryCountdown} segundos..</p>) : (<p>Em {countdown} segundos saberemos seu resultado...</p>)}
          </div>
        </>
      )}

      {conversationFinished && !waiting && (
        (() => {
          const finalLevel = adjustedLevel || getLevel();
          return (
            <div className="text-center">
              <p className="text-xl mb-1">Você acertou {score} de {questions.length} perguntas de múltipla escolha.</p>
              {overallScore !== null ? (
                <>
                  <p className="text-xl mb-2">Pontuação de conversação: {overallScore}</p>
                  <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg mt-4">
                    <p className="text-lg mb-2 font-semibold text-[#42A48B]">Nível final: {finalLevel.level}</p>
                    {/* <p className="mt-2 text-gray-300">{finalLevel.desc}</p> */}
                    {conversationSummary && showConversationSummary && (
                      <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left text-gray-700">
                        <h3 className="font-semibold mb-2">Resumo da conversa:</h3>
                        <p>{conversationSummary}</p>
                      </div>
                    )}
                    {conversationSummary && console.log("Conversation Summary:", conversationSummary)}
                    <button className="p-4 rounded-xl bg-[#42A48B] hover:bg-[#368c73] shadow-lg transform hover:scale-105 transition">
                      <a href={finalLevel.pdf} target="_blank" rel="noopener noreferrer" className="flex items-center">
                        <FaBookOpen className="mr-2" />
                        Baixar plano de estudos ({finalLevel.level})
                      </a>
                    </button>
                  </div>
                  <p className="mt-8 mb-2 text-gray-400">Teve algum problema?</p>
                  <button
                    onClick={() => { setConversationFinished(false); setConversationStarted(false); }}
                    className="p-4 rounded-xl bg-black border-2 border-[#42A48B] text-[#42A48B] hover:bg-gray-900 shadow-lg transform hover:scale-105 transition text-sm">
                    <FaSync className="inline-block mr-1" /> Reiniciar Teste de Conversação
                  </button>
              </>
              ):(
                <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 shadow-lg max-w-xl text-center mx-auto mt-4">
                  <p className="text-gray-100"><FaExclamationCircle className="inline-block text-[#42A48B] mr-2" /> Parece que houve um erro com o seu teste de conversação. Vamos tentar novamente?</p>
                  <button
                    onClick={() => { setConversationFinished(false); setConversationStarted(false); }}
                    className="mt-4 p-4 rounded-xl bg-[#42A48B] hover:bg-[#368c73] shadow-lg transform hover:scale-105 transition">
                    <FaSync className="inline-block mr-1" /> Reiniciar Teste de Conversação
                  </button>
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
  // Extracted VapiWidget block for finished phase
  function renderVapiWidget() {
    // Placeholder text based on state
    const placeholder = hasStartedWidgetOnce
      ? (<><FaPhone className="mr-2" /> Reiniciar teste de conversação</>)
      : (<><FaPhone className="mr-2" /> Iniciar teste de conversação</>);

    const handleWidgetClick = () => {
      setConversationStarted(true);
      setConversationFinished(false);
      if (!hasStartedWidgetOnce) setHasStartedWidgetOnce(true);
    };
    return (
      <div onClick={handleWidgetClick}>
        <VapiWidget
          apiKey="14b5f8ce-9b8c-4432-b917-e8a4bf3e65a2"
          assistantId={getAssistantId(getLevel().level.split(" ")[0], objective)}
          textInputPlaceholder={placeholder ? placeholder : "🎤 Iniciar teste de conversação"}
          onCallEndWithId={async (receivedCallId) => {
            setCallId(receivedCallId);
            console.log("Call ended with id:", receivedCallId);
            setWaiting(true);
            setRetrying(false);

            const fetchSummary = async () => {
              try {
                const url = process.env.NODE_ENV === 'production'
                  ? 'https://natics-api.autonomoon.ai/api/getCallSummary'
                  : 'http://localhost:3001/api/getCallSummary';
                const response = await fetch(url, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ callId: receivedCallId })
                });
                if (!response.ok) throw new Error('Failed to fetch summary');
                const data = await response.json();

                // --- Begin level adjustment logic ---
                const originalLevelObj = getLevel();
                const originalCode = originalLevelObj.level.split(" ")[0];
                const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2"];
                const idx = levelOrder.indexOf(originalCode);
                let parsedOverallScore = Number(data.structuredData.overallScore);
                if (idx === -1) {
                  console.log(`[LEVEL ADJUST] Couldn't find index for code: ${originalCode}, keeping original`);
                  setAdjustedLevel(originalLevelObj);
                } else {
                  let newIdx = idx;
                  let decision = 'keep';
                  if (!Number.isFinite(parsedOverallScore)) {
                    decision = 'invalid-score-keeping-original';
                  } else if (parsedOverallScore >= 6) {
                    decision = 'keep';
                  } else {
                    if (idx === 0) {
                      // already A1
                      newIdx = 0;
                      decision = 'requested-minus-1-but-already-A1-clamped-to-A1';
                    } else {
                      newIdx = idx - 1;
                      decision = 'reduced-by-1-level';
                    }
                  }
                  const finalCode = levelOrder[newIdx];
                  const finalLevelObj = levels.find(l => l.level.startsWith(finalCode)) || originalLevelObj;
                  console.log(`[LEVEL ADJUST] original=${originalCode}, overallScore=${parsedOverallScore}, decision=${decision}, final=${finalCode}`);
                  setAdjustedLevel(finalLevelObj);
                }
                // --- End level adjustment logic ---
                setOverallScore(parsedOverallScore);
                setConversationSummary(data.structuredData.mainRemarks + "<br> Nota: "+ data.structuredData.overallScore);
                setRetrying(false);
                setWaiting(false);
                setConversationFinished(true);
              } catch (error) {
                throw error;
              }
            };

            try {
              // Wait until countdown reaches 0 before fetching summary
              await new Promise(resolve => {
                const checkInterval = setInterval(() => {
                  if (countdownRef.current === 0) {
                    clearInterval(checkInterval);
                    resolve();
                  }
                }, 500);
              });
              await fetchSummary();
            } catch (error) {
              // Retry once after 15 seconds countdown
              setRetrying(true);
              await new Promise(resolve => {
                const retryCheckInterval = setInterval(() => {
                  if (retryCountdownRef.current === 0) {
                    clearInterval(retryCheckInterval);
                    resolve();
                  }
                }, 500);
              });
              try {
                await fetchSummary();
              } catch (error) {
                setConversationSummary("Não foi possível obter o resumo da conversa.");
                setRetrying(false);
                setWaiting(false);
                setConversationFinished(true);
              }
            }
          }}
        />
      </div>
    );
  }
}